import React, { useState, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, Spinner, Table } from "reactstrap"
import api from "API/Api"

/**
 * Shared Manual Payments hook — provides Add Payment + View Payments modals
 * for any loan/advance module.
 *
 * Usage:
 *   const { payBtn, historyBtn, modals } = useManualPayments("vendor_loan", "loanAmount")
 *   // In Actions column:  {payBtn(row)} {historyBtn(row)}
 *   // At bottom of component:  {modals}
 *
 * KEY FIX: `modals` is a JSX element (not a component function). This prevents
 * React from unmounting/remounting on every parent re-render, which was causing
 * the modal to flicker and close on every keystroke.
 */
export default function useManualPayments(moduleType, amountField) {
  // ─── Add Payment modal state ───
  const [payOpen, setPayOpen] = useState(false)
  const [payRow, setPayRow] = useState(null)
  const [payDate, setPayDate] = useState("")
  const [payAmount, setPayAmount] = useState("")
  const [payMode, setPayMode] = useState("Cash")
  const [payRef, setPayRef] = useState("")
  const [payRemarks, setPayRemarks] = useState("")
  const [paySaving, setPaySaving] = useState(false)
  const [payResult, setPayResult] = useState(null)
  const [paySummary, setPaySummary] = useState({ totalPaid: 0, count: 0 })

  // ─── View Payments modal state ───
  const [histOpen, setHistOpen] = useState(false)
  const [histRow, setHistRow] = useState(null)
  const [histData, setHistData] = useState([])
  const [histLoading, setHistLoading] = useState(false)
  const [histSummary, setHistSummary] = useState({ totalPaid: 0, count: 0 })
  const [deleting, setDeleting] = useState(null)

  const fmt = v => { const n = parseFloat(String(v || "0").replace(/,/g, "")); return isNaN(n) ? "₹0" : "₹" + n.toLocaleString("en-IN") }
  const rowAmt = row => parseFloat(String(row?.[amountField] || "0").replace(/,/g, "")) || 0

  // ─── Open Add Payment ───
  const openPay = useCallback(async row => {
    setPayRow(row)
    setPayDate(new Date().toISOString().slice(0, 10))
    setPayAmount("")
    setPayMode("Cash")
    setPayRef("")
    setPayRemarks("")
    setPayResult(null)
    setPaySummary({ totalPaid: 0, count: 0 })
    setPayOpen(true)
    // Fetch existing payment summary for balance display
    try {
      const sRes = await api.get(`manual-payment-summary/${moduleType}/${row.id}`)
      setPaySummary(sRes.data || { totalPaid: 0, count: 0 })
    } catch (e) { /* ignore — balance just shows full amount */ }
  }, [moduleType])

  // ─── Submit payment ───
  const submitPay = async () => {
    if (!payDate || !payAmount || !payMode) {
      setPayResult({ success: false, message: "Date, Amount and Mode are required" }); return
    }
    setPaySaving(true); setPayResult(null)
    try {
      await api.post("create-manual-payment", {
        moduleType, referenceId: payRow.id,
        paymentDate: payDate,
        paymentAmount: parseFloat(payAmount),
        paymentMode: payMode,
        referenceNumber: payRef || null,
        remarks: payRemarks || null,
      })
      setPayResult({ success: true, message: "Payment recorded!" })
      setPayAmount(""); setPayRef(""); setPayRemarks("")
      // Refresh summary so balance updates live
      try {
        const sRes = await api.get(`manual-payment-summary/${moduleType}/${payRow.id}`)
        setPaySummary(sRes.data || { totalPaid: 0, count: 0 })
      } catch (e) { /* ignore */ }
    } catch (e) {
      setPayResult({ success: false, message: e?.response?.data?.message || "Failed to save payment" })
    }
    setPaySaving(false)
  }

  // ─── Open View Payments ───
  const openHist = useCallback(async row => {
    setHistRow(row); setHistOpen(true); setHistLoading(true)
    try {
      const [pRes, sRes] = await Promise.all([
        api.get(`manual-payments/${moduleType}/${row.id}`),
        api.get(`manual-payment-summary/${moduleType}/${row.id}`)
      ])
      setHistData(pRes.data.data || [])
      setHistSummary(sRes.data || { totalPaid: 0, count: 0 })
    } catch (e) { console.error(e) }
    setHistLoading(false)
  }, [moduleType])

  // ─── Delete a payment ───
  const deletePay = async id => {
    setDeleting(id)
    try {
      await api.delete(`delete-manual-payment/${id}`)
      setHistData(prev => {
        const removed = prev.find(p => p.id === id)
        if (removed) {
          setHistSummary(s => ({ totalPaid: s.totalPaid - (removed.paymentAmount || 0), count: s.count - 1 }))
        }
        return prev.filter(p => p.id !== id)
      })
    } catch (e) { console.error(e) }
    setDeleting(null)
  }

  // ─── Icon buttons ───
  const ib = (bg, icon, title, onClick) => (
    <button title={title} onClick={onClick}
      style={{ width: "28px", height: "28px", borderRadius: "4px", background: bg, color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", cursor: "pointer" }}>
      <i className={`fas ${icon}`}></i>
    </button>
  )

  const payBtn = row => ib("#28a745", "fa-rupee-sign", "Add Payment", () => openPay(row))
  const historyBtn = row => ib("#0d6efd", "fa-history", "Payment History", () => openHist(row))

  // ─── Modals as inline JSX (NOT a component function) ───
  // This is the key fix: returning raw JSX means React won't create a new
  // component boundary, so typing in the modal inputs won't cause remount.
  const modals = (<>
    {/* Add Payment Modal */}
    <Modal isOpen={payOpen} toggle={() => setPayOpen(false)} size="md">
      <ModalHeader toggle={() => setPayOpen(false)}><i className="fas fa-rupee-sign me-2" style={{ color: "#28a745" }}></i>Add Manual Payment</ModalHeader>
      <ModalBody>
        {payRow && <div style={{ background: "#f0f9ff", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div><strong>{payRow.vendorName || payRow.producerName || payRow.vehicleNumber || "—"}</strong></div>
            <div><span style={{ color: "#6c757d" }}>Amount: </span><strong style={{ color: "#0284c7" }}>{fmt(rowAmt(payRow))}</strong></div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginTop: "6px", paddingTop: "6px", borderTop: "1px solid #e0f2fe" }}>
            <div><span style={{ color: "#6c757d" }}>Paid: </span><strong style={{ color: "#22c55e" }}>{fmt(paySummary.totalPaid)}</strong>
              <span style={{ color: "#6c757d", marginLeft: "6px", fontSize: "11px" }}>({paySummary.count} payment{paySummary.count !== 1 ? "s" : ""})</span></div>
            <div><span style={{ color: "#6c757d" }}>Balance: </span><strong style={{ color: "#dc2626" }}>{fmt(rowAmt(payRow) - paySummary.totalPaid)}</strong></div>
          </div>
        </div>}
        <div className="mb-3"><label className="form-label fw-bold">Payment Date *</label>
          <input type="date" className="form-control form-control-sm" value={payDate} onChange={e => setPayDate(e.target.value)} /></div>
        <div className="mb-3"><label className="form-label fw-bold">Payment Amount *</label>
          <input type="number" className="form-control form-control-sm" placeholder="0.00" min="0.01" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} /></div>
        <div className="mb-3"><label className="form-label fw-bold">Payment Mode *</label>
          <select className="form-control form-control-sm" value={payMode} onChange={e => setPayMode(e.target.value)}>
            {["Cash", "Bank Transfer", "Cheque", "UPI", "Other"].map(m => <option key={m} value={m}>{m}</option>)}
          </select></div>
        <div className="mb-3"><label className="form-label fw-bold">Reference Number</label>
          <input type="text" className="form-control form-control-sm" placeholder="Cheque/Transaction number" value={payRef} onChange={e => setPayRef(e.target.value)} /></div>
        <div className="mb-3"><label className="form-label fw-bold">Remarks</label>
          <textarea className="form-control form-control-sm" rows={2} value={payRemarks} onChange={e => setPayRemarks(e.target.value)} /></div>
        {payResult && <div className={`alert alert-${payResult.success ? "success" : "danger"} py-2`} style={{ fontSize: "13px" }}>{payResult.message}</div>}
        <div className="text-end"><button className="btn btn-success btn-sm" onClick={submitPay} disabled={paySaving}>
          {paySaving ? <><Spinner size="sm" /> Saving...</> : <><i className="fas fa-save me-1"></i>Save Payment</>}</button></div>
      </ModalBody>
    </Modal>

    {/* View Payment History Modal */}
    <Modal isOpen={histOpen} toggle={() => setHistOpen(false)} size="lg">
      <ModalHeader toggle={() => setHistOpen(false)}><i className="fas fa-history me-2" style={{ color: "#0d6efd" }}></i>Payment History</ModalHeader>
      <ModalBody>
        {histRow && <div style={{ background: "#f0f9ff", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div><strong>{histRow.vendorName || histRow.producerName || histRow.vehicleNumber || "—"}</strong></div>
            <div><span style={{ color: "#6c757d" }}>Original: </span><strong style={{ color: "#0284c7" }}>{fmt(rowAmt(histRow))}</strong>
              <span style={{ color: "#6c757d", marginLeft: "12px" }}>Paid: </span><strong style={{ color: "#22c55e" }}>{fmt(histSummary.totalPaid)}</strong>
              <span style={{ color: "#6c757d", marginLeft: "12px" }}>Balance: </span><strong style={{ color: "#dc2626" }}>{fmt(rowAmt(histRow) - histSummary.totalPaid)}</strong>
            </div>
          </div>
        </div>}
        {histLoading ? <div className="text-center py-4"><Spinner /><p className="mt-2 text-muted">Loading payments...</p></div>
          : histData.length > 0 ? <div className="table-responsive"><Table className="table table-sm table-bordered mb-0" style={{ fontSize: "13px" }}>
            <thead style={{ background: "#f8fafc" }}><tr>
              <th>#</th><th>Date</th><th>Amount</th><th>Mode</th><th>Reference</th><th>Remarks</th><th style={{ width: "60px" }}>Actions</th>
            </tr></thead>
            <tbody>{histData.map((p, i) => <tr key={p.id}>
              <td>{i + 1}</td>
              <td>{p.paymentDate}</td>
              <td style={{ fontWeight: "600", color: "#22c55e" }}>{fmt(p.paymentAmount)}</td>
              <td><span style={{ background: p.paymentMode === "Cash" ? "#dcfce7" : p.paymentMode === "UPI" ? "#dbeafe" : "#fef3c7", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{p.paymentMode}</span></td>
              <td>{p.referenceNumber || "—"}</td>
              <td>{p.remarks || "—"}</td>
              <td><button className="btn btn-sm btn-outline-danger" style={{ width: "28px", height: "28px", padding: 0 }} onClick={() => deletePay(p.id)} disabled={deleting === p.id}>
                {deleting === p.id ? <Spinner size="sm" /> : <i className="fas fa-trash" style={{ fontSize: "11px" }}></i>}
              </button></td>
            </tr>)}</tbody>
            <tfoot style={{ background: "#f1f5f9", fontWeight: "700" }}><tr>
              <td colSpan={2}>Total ({histSummary.count} payments)</td>
              <td style={{ color: "#22c55e" }}>{fmt(histSummary.totalPaid)}</td>
              <td colSpan={4}><span style={{ color: "#dc2626" }}>Remaining: {fmt(rowAmt(histRow) - histSummary.totalPaid)}</span></td>
            </tr></tfoot>
          </Table></div>
          : <div className="text-center py-4"><i className="fas fa-inbox fa-2x text-muted mb-2 d-block"></i><p className="text-muted">No payments recorded yet</p></div>}
      </ModalBody>
    </Modal>
  </>)

  return { payBtn, historyBtn, modals }
}
